import type { DealerDisarmSiteRequest, DealerTypeValue } from '../../models/entities/dealer-api.model';
import type { SiteDisarmReasonsResponse } from '../../models/entities/site-disarm-reasons.model';
import type { DisarmSiteRequest, SiteDisarmFor } from '../../models/entities/site-arm-disarm.model';

/** Maps `GET /dealer/types?names=DisarmSiteReason` into the disarm popover contract. */
export function mapDealerTypeValuesToDisarmReasons(
  items: DealerTypeValue[] | null | undefined,
): SiteDisarmReasonsResponse {
  return {
    items: (items ?? [])
      .filter((item) => Number.isInteger(Number(item.id)) && Number(item.id) > 0)
      .map((item) => ({
        id: String(item.id),
        label: item.name?.trim() ?? String(item.id),
      })),
  };
}

/**
 * Dealer `disarmTypeId` values for camera and site disarm POSTs.
 * Until Next Schedule = 0, Fixed Time Period = 1, Date & Time = 2.
 */
export const DEFAULT_DEALER_DISARM_TYPE_ID: Record<SiteDisarmFor, number> = {
  'until-next-schedule': 0,
  'fixed-time': 1,
  'date-time': 2,
};

/** @deprecated Use `DEFAULT_DEALER_DISARM_TYPE_ID` — kept for existing specs/imports. */
export const DEALER_DISARM_TYPE_ID = DEFAULT_DEALER_DISARM_TYPE_ID;

function normalizeDisarmTypeName(name: string | null | undefined): string {
  return (name ?? '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '');
}

/** Maps `GET /dealer/types?names=DisarmType` into UI disarm-for options. */
export function mapDealerTypeValuesToDisarmTypeIds(
  items: DealerTypeValue[] | null | undefined,
): Partial<Record<SiteDisarmFor, number>> {
  const byName = new Map<string, number>();

  for (const item of items ?? []) {
    const id = Number(item.id);
    if (!Number.isInteger(id) || id <= 0) {
      continue;
    }

    const normalized = normalizeDisarmTypeName(item.name);
    if (normalized) {
      byName.set(normalized, id);
    }
  }

  const findId = (...candidates: string[]): number | undefined => {
    for (const candidate of candidates) {
      const exact = byName.get(candidate);
      if (exact != null) {
        return exact;
      }
    }

    for (const [name, id] of byName) {
      if (candidates.some((candidate) => name.includes(candidate) || candidate.includes(name))) {
        return id;
      }
    }

    return undefined;
  };

  const untilNextSchedule = findId('untilnextschedule', 'nextschedule', 'schedule');
  const fixedTime = findId('fixedtimeperiod', 'fixedtime', 'fixedduration', 'duration', 'timed');
  const dateTime = findId('datetime', 'dateandtime', 'date', 'specificdate', 'customdate');

  const mapped: Partial<Record<SiteDisarmFor, number>> = {};

  if (untilNextSchedule != null) {
    mapped['until-next-schedule'] = untilNextSchedule;
  }
  if (fixedTime != null) {
    mapped['fixed-time'] = fixedTime;
  }
  if (dateTime != null) {
    mapped['date-time'] = dateTime;
  } else if (fixedTime != null) {
    // Dealer often exposes one timed disarm type for both fixed duration and explicit end.
    mapped['date-time'] = fixedTime;
  } else if (untilNextSchedule != null && byName.size === 2) {
    const timedTypeId = [...byName.values()].find((id) => id !== untilNextSchedule);
    if (timedTypeId != null) {
      mapped['fixed-time'] = timedTypeId;
      mapped['date-time'] = timedTypeId;
    }
  }

  return mapped;
}

export function resolveDealerDisarmTypeId(
  disarmFor: SiteDisarmFor,
  typeIds?: Partial<Record<SiteDisarmFor, number>>,
): number {
  const fromApi = typeIds?.[disarmFor];
  if (fromApi != null) {
    return fromApi;
  }

  return DEFAULT_DEALER_DISARM_TYPE_ID[disarmFor];
}

/** Maps Agent UI disarm popover payload into Dealer POST /sites/{siteId}/disarm. */
export function mapDisarmSiteRequestToDealer(
  body: DisarmSiteRequest,
  options?: { now?: Date; typeIds?: Partial<Record<SiteDisarmFor, number>> },
): DealerDisarmSiteRequest {
  const now = options?.now ?? new Date();
  const reasonTypeId = Number(body.reasonId);
  if (!Number.isInteger(reasonTypeId) || reasonTypeId <= 0) {
    throw new Error('Invalid disarm reason id');
  }

  const request: DealerDisarmSiteRequest = {
    reasonTypeId,
    disarmTypeId: resolveDealerDisarmTypeId(body.disarmFor, options?.typeIds),
  };

  if (body.disarmFor === 'fixed-time') {
    const durationMinutes = body.durationMinutes;
    if (durationMinutes == null || durationMinutes <= 0) {
      throw new Error('durationMinutes is required for fixed-time disarm');
    }

    const end = new Date(now.getTime() + durationMinutes * 60_000);
    request.durationMinutes = durationMinutes;
    request.startDateTime = now.toISOString();
    request.endDateTime = end.toISOString();
  }

  if (body.disarmFor === 'date-time') {
    if (!body.untilAt?.trim()) {
      throw new Error('untilAt is required for date-time disarm');
    }

    const endMs = Date.parse(body.untilAt);
    if (Number.isNaN(endMs)) {
      throw new Error('Invalid disarm untilAt timestamp');
    }

    request.startDateTime = now.toISOString();
    request.endDateTime = new Date(endMs).toISOString();
  }

  return request;
}
